import { useState, useRef, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../auth/AuthContext';

interface NavItem {
  path: string;
  label: string;
  icon: string;
  adminOnly?: boolean;
}

const navItems: NavItem[] = [
  { path: '/admin', label: 'דשבורד ראשי', icon: 'ri-dashboard-3-line' },
  { path: '/admin/activities', label: 'פעילויות', icon: 'ri-calendar-event-line' },
  { path: '/admin/jobs', label: 'משרות', icon: 'ri-briefcase-line' },
  { path: '/admin/users', label: 'נרשמים', icon: 'ri-group-line' },
  { path: '/admin/partners', label: 'שותפים', icon: 'ri-shake-hands-line', adminOnly: true },
  { path: '/admin/stories', label: 'סטוריז', icon: 'ri-instance-line', adminOnly: true },
  { path: '/admin/locations', label: 'מוקדים', icon: 'ri-map-pin-line', adminOnly: true },
  { path: '/admin/permissions', label: 'הרשאות משתמשים', icon: 'ri-shield-user-line', adminOnly: true },
  { path: '/admin/settings', label: 'הגדרות', icon: 'ri-settings-3-line', adminOnly: true },
];

export default function AdminSidebar({ mobileOpen, onClose }: { mobileOpen: boolean; onClose: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAdmin, logout } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setUserMenuOpen(false);
    onClose();
  }, [location.pathname]);

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout();
    navigate('/login');
  };

  const visibleItems = navItems.filter((item) => !item.adminOnly || isAdmin);

  return (
    <>
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/20 z-20 md:hidden" onClick={onClose} />
      )}

      <aside
        className={`fixed right-0 top-0 h-full w-64 bg-white border-l-2 border-rose-100 flex flex-col z-30 transition-transform duration-300 md:translate-x-0 ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        dir="rtl"
      >
        {/* Logo */}
        <div className="px-6 py-4 border-b-2 border-rose-100 flex items-center justify-between">
          <img
            src="https://storage.readdy-site.link/project_files/d20edc57-a850-4bd5-be1a-2dc2615daee8/f24373d3-615b-4526-a5fc-5e05b464179e_----.png?v=aee108af124d23a6400d2fdc7786381b"
            alt="לוגו"
            className="h-12 w-auto object-contain"
          />
          <button onClick={onClose} className="md:hidden w-8 h-8 flex items-center justify-center rounded-xl border-2 border-gray-200 text-gray-400 hover:bg-gray-50 cursor-pointer">
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <ul className="space-y-1">
            {visibleItems.map((item) => {
              const isActive =
                item.path === '/admin'
                  ? location.pathname === '/admin'
                  : location.pathname.startsWith(item.path);
              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      isActive
                        ? 'bg-rose-50 text-rose-500 border-2 border-rose-200'
                        : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700 border-2 border-transparent'
                    }`}
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${item.icon} text-base`}></i>
                    </div>
                    <span>{item.label}</span>
                    {isActive && (
                      <div className="mr-auto w-1.5 h-1.5 rounded-full bg-rose-400"></div>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer */}
        <div className="px-4 py-4 border-t-2 border-rose-100" ref={menuRef}>
          <div
            className={`overflow-hidden transition-all duration-200 ease-in-out mb-2 ${
              userMenuOpen ? 'max-h-32 opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="bg-white border-2 border-rose-100 rounded-xl overflow-hidden">
              {isAdmin && (
                <>
                  <button
                    onClick={() => { setUserMenuOpen(false); navigate('/admin/settings'); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-semibold text-gray-600 hover:bg-rose-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-4 h-4 flex items-center justify-center text-rose-400">
                      <i className="ri-settings-3-line text-sm"></i>
                    </div>
                    הגדרות
                  </button>
                  <div className="border-t border-rose-50"></div>
                </>
              )}
              <button
                onClick={() => { setUserMenuOpen(false); navigate('/admin/profile'); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-semibold text-gray-600 hover:bg-rose-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center text-rose-400">
                  <i className="ri-user-line text-sm"></i>
                </div>
                פרופיל
              </button>
              <div className="border-t border-rose-50"></div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-semibold text-gray-500 hover:bg-red-50 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-logout-box-r-line text-sm"></i>
                </div>
                התנתקות
              </button>
            </div>
          </div>

          <button
            onClick={() => setUserMenuOpen((v) => !v)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 transition-all cursor-pointer ${
              userMenuOpen
                ? 'bg-rose-100 border-rose-200'
                : 'bg-rose-50 border-rose-100 hover:bg-rose-100 hover:border-rose-200'
            }`}
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-rose-200 text-rose-600 text-sm font-bold flex-shrink-0">
              {user?.name?.charAt(0) || 'א'}
            </div>
            <div className="flex-1 min-w-0 text-right">
              <p className="text-xs font-semibold text-gray-700 truncate">{user?.name || 'אדמין ראשי'}</p>
              <p className="text-xs text-gray-400 truncate">{user?.email || 'admin@platform.co.il'}</p>
            </div>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400 flex-shrink-0">
              <i className={`ri-arrow-up-s-line text-sm transition-transform duration-200 ${userMenuOpen ? 'rotate-0' : 'rotate-180'}`}></i>
            </div>
          </button>
        </div>
      </aside>
    </>
  );
}
